import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'
import type { ApiFetch } from '@/lib/api'

// --- Types ---

export type FitScore = 'high' | 'medium' | 'low'

export type LinkedInProfile = {
  readonly id: string
  readonly name: string
  readonly title: string
  readonly company: string
  readonly location: string
  readonly mutual_connections: number
  readonly avatar_url?: string
  readonly linkedin_url: string
  readonly fit_score: FitScore
  readonly fit_score_value: number
  readonly in_pipeline: boolean
  // Apollo preview/enrich flow. `apollo_id` is non-null for results that came
  // out of /leads/search/apollo (and are eligible for paid enrichment).
  // `is_enriched=false` means the row is a preview — no LinkedIn URL yet, can't
  // be added to the pipeline until the user spends a credit. Demo results and
  // Unipile results come back already enriched.
  readonly apollo_id: string | null
  readonly is_enriched: boolean
}

export type SearchFilters = {
  readonly title?: string
  readonly company?: string
  readonly industry?: string
  readonly location?: string
  readonly keywords?: string
}

export type SearchResults = {
  readonly results: readonly LinkedInProfile[]
  readonly next_cursor: string | null
  readonly total: number
  readonly note?: string
  readonly demo_mode?: boolean
  /**
   * When Apollo (the previews provider) errors but the LinkedIn/Unipile
   * search succeeds, we still return the Unipile results so the user has
   * something to act on. apollo_error captures the reason so the UI can
   * show a non-blocking banner ("Apollo previews unavailable: rate limit
   * hit / API key issue / ..."). Empty when Apollo succeeded.
   */
  readonly apollo_error?: string
}

export type NewLead = {
  readonly linkedin_url: string
  readonly first_name: string
  readonly last_name: string
  readonly title?: string
  readonly company?: string
  readonly location?: string
}

// --- API functions (accept apiFetch as parameter) ---

// Backend search result shape (from Unipile or mock)
type BackendSearchResult = {
  readonly linkedin_url: string | null
  readonly linkedin_id: string | null
  readonly first_name: string | null
  readonly last_name: string | null
  readonly full_name: string | null
  readonly headline: string | null
  readonly company: string | null
  readonly title: string | null
  readonly industry: string | null
  readonly location: string | null
  readonly avatar_url: string | null
  readonly connection_degree: string | null
  readonly icp_score: number
  readonly intent_score: number
  readonly composite_score: number
  readonly in_pipeline?: boolean
  readonly apollo_id?: string | null
  readonly is_enriched?: boolean
}

// Backend search response shape (Apollo)
type BackendSearchResponse = {
  readonly results: readonly BackendSearchResult[]
  readonly cursor: string | null
  readonly has_more: boolean
  readonly note?: string
  readonly demo_mode?: boolean
}

function mapFitScore(score: number): FitScore {
  if (score >= 70) return 'high'
  if (score >= 40) return 'medium'
  return 'low'
}

function mapBackendProfile(item: BackendSearchResult): LinkedInProfile {
  const fullName =
    item.full_name ??
    ([item.first_name, item.last_name].filter(Boolean).join(' ') || 'Unknown')
  const fitScoreValue = item.composite_score ?? item.icp_score ?? 0
  // Stable card id: prefer linkedin_url (post-enrich) → apollo_id (pre-enrich
  // preview) → linkedin_id → name. Using apollo_id for previews means the
  // card identity stays stable across the enrich call so React doesn't
  // unmount + remount during the optimistic update.
  const cardId = item.linkedin_url ?? item.apollo_id ?? item.linkedin_id ?? fullName
  return {
    id: cardId,
    name: fullName,
    title: item.title ?? item.headline ?? '',
    company: item.company ?? '',
    location: item.location ?? '',
    mutual_connections: 0, // not provided by backend
    avatar_url: item.avatar_url ?? undefined,
    linkedin_url: item.linkedin_url ?? '',
    fit_score: mapFitScore(fitScoreValue),
    fit_score_value: fitScoreValue,
    in_pipeline: item.in_pipeline ?? false,
    apollo_id: item.apollo_id ?? null,
    is_enriched: item.is_enriched ?? true,
  }
}

async function searchUnipile(
  apiFetch: ApiFetch,
  filters: SearchFilters,
  accountId: string,
  userConfig?: Record<string, unknown>,
): Promise<readonly LinkedInProfile[]> {
  // LinkedIn keyword search is more useful with ONE compact term than
  // every filter slammed into one long phrase, which over-narrows the
  // match set down to zero on basic (non-Sales-Nav) accounts. We prefer
  // job titles, fall back to keywords, then industries, then a generic
  // term as last resort.
  const query =
    (filters.title && filters.title.trim()) ||
    (filters.keywords && filters.keywords.trim()) ||
    (filters.industry && filters.industry.trim()) ||
    'professional'

  try {
    const res = await apiFetch<BackendSearchResponse>('/api/leads/search', {
      method: 'POST',
      body: {
        query,
        filters: {
          // The worker joins these into ONE LinkedIn keyword phrase. On
          // basic (non-Sales-Nav) accounts that's enough to zero the
          // result set if we feed it title + industry + keywords +
          // location all at once. Pass only title so LinkedIn returns
          // something, then rely on the worker's location post-filter
          // (which DOES read geo_urn) for region narrowing.
          title: filters.title,
          geo_urn: filters.location,
        },
        account_id: accountId,
        ...(userConfig ? { user_config: userConfig } : {}),
      },
    })
    if (!res.success || !res.data) {
      console.warn('[searchUnipile] failed:', res.error ?? '(no error message)')
      return []
    }
    if (res.data.results.length === 0) {
      console.info('[searchUnipile] returned 0 results', {
        query,
        account_id_suffix: accountId.slice(-6),
      })
    }
    return res.data.results.map(mapBackendProfile)
  } catch (err) {
    // If the LinkedIn side fails (rate-limit, transient timeout, etc) we
    // fall back to the provider previews alone rather than failing the
    // whole search. Log so this is visible in DevTools when debugging.
    console.warn('[searchUnipile] threw:', err)
    return []
  }
}

async function searchLeads(
  apiFetch: ApiFetch,
  filters: SearchFilters,
  cursor?: string | null,
  userConfig?: Record<string, unknown>,
  accountId?: string | null,
): Promise<SearchResults> {
  // Run LinkedIn-native search (free, already-enriched) and provider-side
  // previews (paid enrich-on-add) in parallel. Native results show first
  // so the user always has something they can add immediately while the
  // larger preview list queues underneath for credit-based enrichment.
  const queryParts = [
    filters.title,
    filters.company,
    filters.industry,
    filters.keywords,
    filters.location,
  ].filter(Boolean)
  const query = queryParts.join(' ').trim() || 'professional'

  const [unipileResults, providerResp] = await Promise.all([
    accountId
      ? searchUnipile(apiFetch, filters, accountId, userConfig)
      : Promise.resolve([] as readonly LinkedInProfile[]),
    apiFetch<BackendSearchResponse>('/api/leads/search/apollo', {
      method: 'POST',
      body: {
        query,
        filters: {
          title: filters.title,
          company_name: filters.company,
          industry: filters.industry,
          geo_urn: filters.location,
          keywords: filters.keywords,
        },
        cursor: cursor ?? undefined,
        ...(userConfig ? { user_config: userConfig } : {}),
      },
    }),
  ])

  if (!providerResp.success || !providerResp.data) {
    // Provider search failed. If LinkedIn returned results we still show
    // them; otherwise surface the provider error so the user sees it.
    const apolloErrorMessage = providerResp.error
      ?? (providerResp as { error_code?: string }).error_code
      ?? 'Preview search temporarily unavailable. Please try again.'
    console.warn('[searchLeads] Apollo provider failed:', apolloErrorMessage)
    if (unipileResults.length > 0) {
      return {
        results: unipileResults,
        next_cursor: null,
        total: unipileResults.length,
        apollo_error: apolloErrorMessage,
      }
    }
    throw new Error(apolloErrorMessage)
  }

  const raw = providerResp.data
  const providerResults = raw.results.map(mapBackendProfile)

  // Dedup by linkedin_url: if a profile appears in both, keep the LinkedIn
  // copy (it's already enriched and free). Provider previews have no
  // linkedin_url until enriched so this only catches the rare case where
  // a provider row came back pre-enriched.
  const seenUrls = new Set(
    unipileResults
      .map((r) => r.linkedin_url)
      .filter((u): u is string => Boolean(u)),
  )
  const providerDeduped = providerResults.filter(
    (r) => !r.linkedin_url || !seenUrls.has(r.linkedin_url),
  )

  return {
    results: [...unipileResults, ...providerDeduped],
    next_cursor: raw.cursor,
    total: unipileResults.length + providerDeduped.length,
    note: raw.note,
    demo_mode: raw.demo_mode,
  }
}

async function importLead(
  apiFetch: ApiFetch,
  lead: NewLead,
): Promise<{ readonly id: string }> {
  const res = await apiFetch<{ id: string }>('/api/leads', { method: 'POST', body: lead })
  if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to import lead')
  return res.data
}

async function bulkImportLeads(
  apiFetch: ApiFetch,
  leads: readonly NewLead[],
): Promise<{ readonly count: number; readonly ids: readonly string[] }> {
  const res = await apiFetch<{ created: number; leads: ReadonlyArray<{ id: string }> }>('/api/leads/bulk', {
    method: 'POST',
    body: { leads },
  })
  if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to bulk import leads')
  // Surface the created lead ids so the caller can offer "Add to Campaign"
  // right after import (the search-result card ids are LinkedIn URLs, not DB
  // lead ids, so we must use what the worker actually created).
  const ids = (res.data.leads ?? []).map((l) => l.id).filter(Boolean)
  return { count: res.data.created, ids }
}

export type EnrichResponse = {
  readonly enriched: readonly LinkedInProfile[]
  readonly requested: number
  readonly matched: number
  readonly credits_charged: number
  readonly balance_after: number
  /** How many enriched leads the worker auto-imported into the user's lead database. */
  readonly auto_imported: number
  /** How many were served from the shared enriched pool (no fresh Apollo call). */
  readonly from_pool: number
  /** How many were already in the user's database (returned free, not charged). */
  readonly already_owned: number
  /** How many could not be auto-imported because the plan's DB capacity is full. */
  readonly skipped_at_capacity: number
}

async function enrichApolloLeads(
  apiFetch: ApiFetch,
  apolloIds: readonly string[],
  userConfig?: Record<string, unknown>,
): Promise<EnrichResponse> {
  type RawEnrichData = {
    readonly enriched: readonly BackendSearchResult[]
    readonly requested: number
    readonly matched: number
    readonly credits_charged: number
    readonly balance_after: number
    readonly auto_imported?: number
    readonly from_pool?: number
    readonly already_owned?: number
    readonly skipped_at_capacity?: number
  }
  const res = await apiFetch<RawEnrichData>('/api/leads/search/enrich/apollo', {
    method: 'POST',
    body: {
      apollo_ids: apolloIds,
      ...(userConfig ? { user_config: userConfig } : {}),
    },
  })
  if (!res.success || !res.data) {
    // The worker returns a human-readable error like "You need 3 credits but
    // only have 1" — show that verbatim instead of inventing a generic copy.
    throw new Error(res.error ?? 'Failed to enrich leads')
  }
  return {
    enriched: res.data.enriched.map(mapBackendProfile),
    requested: res.data.requested,
    matched: res.data.matched,
    credits_charged: res.data.credits_charged,
    balance_after: res.data.balance_after,
    auto_imported: res.data.auto_imported ?? 0,
    from_pool: res.data.from_pool ?? 0,
    already_owned: res.data.already_owned ?? 0,
    skipped_at_capacity: res.data.skipped_at_capacity ?? 0,
  }
}

// --- Hooks ---

export function useLeadSearch(
  filters: SearchFilters,
  cursor?: string | null,
  userConfig?: Record<string, unknown>,
  accountId?: string | null,
) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: ['leads', 'search', filters, cursor, userConfig, accountId],
    queryFn: () => searchLeads(apiFetch, filters, cursor, userConfig, accountId),
    enabled: Object.values(filters).some((v) => typeof v === 'string' && v.trim().length > 0),
    staleTime: 60_000,
  })
}

export function useImportLead() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (lead: NewLead) => importLead(apiFetch, lead),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

export function useBulkImport() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (leads: readonly NewLead[]) => bulkImportLeads(apiFetch, leads),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

export function useEnrichApolloLeads() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      apollo_ids,
      user_config,
    }: {
      readonly apollo_ids: readonly string[]
      readonly user_config?: Record<string, unknown>
    }) => enrichApolloLeads(apiFetch, apollo_ids, user_config),
    onSuccess: () => {
      // Refresh the credit-bar so the user sees their new balance immediately.
      void queryClient.invalidateQueries({ queryKey: ['credits'] })
      // Enrichment now auto-imports into the lead database — refresh the
      // Leads page so the new rows appear without a manual reload.
      void queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

// --- Utilities ---

export function profileToNewLead(profile: LinkedInProfile): NewLead {
  const nameParts = profile.name.split(' ')
  return {
    linkedin_url: profile.linkedin_url,
    first_name: nameParts[0] ?? profile.name,
    last_name: nameParts.slice(1).join(' ') || '',
    title: profile.title,
    company: profile.company,
    location: profile.location,
  }
}
